/**
 * `useIncidentsToClose(actor, source)` (13.2-08; D-13.2-A-1, D-13.2-P-8): Oversight's "Incidents to close" on Verify.
 * Every Evaluating incident (readied by a Care Manager, waiting for Oversight), each with 13.2-06's checklist, who
 * readied it and when.
 *
 *   - **Three reads, never one per incident:** the Incidents list read the lens already makes (`listWaitingRecords`,
 *     whose columns include every one `incidentChecklist` reads), ONE Incidents Audit Log read for who readied each
 *     (`listIncidentReadied`, the preparer read), and ONE Action Register read for every incident's follow-ups
 *     (`listActionRows`, linked client-side by `followUpsOf`).
 *   - **Fail closed.** A failed follow-up read leaves both follow-up checks open ("couldn't be read"), never passing.
 *     A failed readier read, or an incident whose readier isn't on its trail, is never "ready for you". The
 *     incidents read failing is an error, said plainly, never an empty "nothing to close".
 *   - **Ready for you** (`readyCount`, the Verify badge's addition): every check passes, the readier is known, and it
 *     isn't the viewer (D-13-L-19: someone else approves).
 *   - **Oversight only.** For anyone else the hook reads nothing and is `idle`.
 *   - In practice mode the source adds the person's own practice incidents, as Today does (`liveRecordsSource`).
 *
 * The source is injected (`IncidentsToCloseSource`), so design/verify-preview and the tests use synthetic rows.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { liveChoice } from '../config/fieldset/live';
import { isOversight } from '../lib/actionAuthority';
import { followUpsOf } from '../lib/followUps';
import { CHECK_LABELS, incidentChecklist, openChecks } from '../lib/incidentVerify';
import type { CheckItem, CheckKey } from '../lib/incidentVerify';
import { isPracticeRef } from '../lib/recordQuery';
import { listActionRows } from '../services/actionService';
import { listIncidentReadied, readiedBy } from '../services/recordService';
import type { Readied, RecordIO } from '../services/recordService';
import { practiceRecordStore, readPracticeRecord } from '../services/commitAdapter';
import type { PracticeRecordStore } from '../services/commitAdapter';
import { liveRecordsSource } from './useRecordsWaiting';
import type { ActionRow, Actor } from '../types/actionRegister';
import type { RecordRow } from '../types/records';

/** The focus refetch floor, as Today's (D-13-09-3). */
export const INCIDENTS_TO_CLOSE_REFETCH_MIN_MS = 60_000;

const [EVALUATING] = liveChoice('incidents', 'Status', 'Evaluating');

/** Where the rows come from: Graph in the app, synthetic rows in the preview and tests. */
export interface IncidentsToCloseSource {
  /** Every Incidents row, with at least the columns the checklist reads. Throws on a failed read. */
  listIncidents(): Promise<RecordRow[]>;
  /** Who readied each incident and when, by ref (one read). */
  listReadied(): Promise<Readonly<Record<string, Readied | undefined>>>;
  /** Every Action Register row (one read), for the follow-ups. */
  listActions(): Promise<ActionRow[]>;
}

/** The app's source: the live reads, plus (practice mode) the person's own practice incidents. */
export function liveIncidentsToCloseSource(
  io: RecordIO,
  opts: { practiceUpn?: string | null; practiceStore?: PracticeRecordStore } = {},
): IncidentsToCloseSource {
  const records = liveRecordsSource(io, opts);
  const upn = (opts.practiceUpn ?? '').trim().toLowerCase();
  const practiceReadied = (): Record<string, Readied | undefined> => {
    if (!upn) return {};
    const store = opts.practiceStore ?? practiceRecordStore();
    const out: Record<string, Readied | undefined> = {};
    for (const rec of store.list()) {
      if (rec.register !== 'incidents' || rec.upn !== upn) continue;
      const read = readPracticeRecord(rec.ref, store);
      if (read) out[rec.ref] = readiedBy(read.siblings);
    }
    return out;
  };
  return {
    listIncidents: () => records.listRegister('incidents'),
    listReadied: async () => ({ ...(await listIncidentReadied(io)), ...practiceReadied() }),
    listActions: () => listActionRows(io),
  };
}

export interface IncidentToClose {
  row: RecordRow;
  checks: CheckItem[];
  /** The labels of the open checks (`openChecks`): what approve-and-close would refuse on. */
  open: string[];
  /** Who readied it (lower-cased UPN), or null when unknown. */
  readiedBy: string | null;
  readiedAt: string | null;
  /** The viewer readied it, so someone else approves (D-13-L-19). */
  readiedByYou: boolean;
  /** Every check passes, the readier is known, and it isn't the viewer. */
  ready: boolean;
}

export type IncidentsToCloseStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface UseIncidentsToCloseResult {
  status: IncidentsToCloseStatus;
  /** Oldest readied first; an unknown readied time last. */
  items: IncidentToClose[];
  /** How many are ready for this viewer to close, or `undefined` until known (the nav badge adds it). */
  readyCount: number | undefined;
  error: string | null;
  /** The follow-up read failed: the follow-up checks are open on every card. */
  followUpsNote: string | null;
  /** The readier read failed: no card is "ready", and the record page checks who readied it. */
  readiedNote: string | null;
  reload: () => void;
}

interface Loaded {
  key: string;
  readAt: number;
  rows: RecordRow[] | null;
  readied: Readonly<Record<string, Readied | undefined>> | null;
  actions: ActionRow[] | null;
  error: string | null;
}

const FOLLOW_UP_CHECKS: ReadonlySet<CheckKey> = new Set<CheckKey>(['follow-ups-current', 'worker-update']);
const FOLLOW_UPS_UNREAD = "The follow-ups couldn't be read, so this can't be checked yet.";

const message = (err: unknown) => (err instanceof Error ? err.message : 'The incidents could not be read.');
const lower = (v: string | null | undefined) => (v ?? '').trim().toLowerCase();

function checksOf(row: RecordRow, actions: ActionRow[] | null, now: Date): CheckItem[] {
  if (actions === null) {
    // Fail closed: a check that reads the follow-ups is open, never passing on a read that failed. (The worker-update
    // check passes without them when no worker was injured, so only its failing form is reworded.)
    return incidentChecklist(row, [], now).map((c) =>
      FOLLOW_UP_CHECKS.has(c.key) && (c.key === 'follow-ups-current' || !c.ok)
        ? { ...c, label: CHECK_LABELS[c.key], ok: false, detail: FOLLOW_UPS_UNREAD }
        : c,
    );
  }
  const followUps = isPracticeRef(row.ref) ? [] : followUpsOf(row.ref, actions, now);
  return incidentChecklist(row, followUps, now);
}

export function useIncidentsToClose(
  actor: Actor | null,
  source: IncidentsToCloseSource | null,
  opts: { now?: () => Date } = {},
): UseIncidentsToCloseResult {
  const nowRef = useRef(opts.now);
  useEffect(() => {
    nowRef.current = opts.now;
  });
  const [trigger, setTrigger] = useState(0);
  const lastLoad = useRef(0);
  const [loaded, setLoaded] = useState<Loaded | null>(null);

  const enabled = !!actor && !!source && isOversight(actor);
  const who = actor ? lower(actor.upn) : '';
  const key = `${who}|${trigger}`;

  useEffect(() => {
    if (!enabled || !source) return;
    let cancelled = false;
    const readAt = (nowRef.current ? nowRef.current() : new Date()).getTime();
    lastLoad.current = Date.now();
    const read = async (): Promise<Loaded> => {
      const [rows, readied, actions] = await Promise.allSettled([source.listIncidents(), source.listReadied(), source.listActions()]);
      if (rows.status === 'rejected') return { key, readAt, rows: null, readied: null, actions: null, error: message(rows.reason) };
      return {
        key,
        readAt,
        rows: rows.value,
        readied: readied.status === 'fulfilled' ? readied.value : null,
        actions: actions.status === 'fulfilled' ? actions.value : null,
        error: null,
      };
    };
    void read().then((result) => {
      if (!cancelled) setLoaded(result);
    });
    return () => {
      cancelled = true;
    };
  }, [enabled, source, key]);

  const reload = useCallback(() => setTrigger((n) => n + 1), []);

  useEffect(() => {
    if (!enabled) return;
    const onFocus = () => {
      if (Date.now() - lastLoad.current >= INCIDENTS_TO_CLOSE_REFETCH_MIN_MS) reload();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [enabled, reload]);

  return useMemo<UseIncidentsToCloseResult>(() => {
    const base: UseIncidentsToCloseResult = {
      status: 'idle',
      items: [],
      readyCount: undefined,
      error: null,
      followUpsNote: null,
      readiedNote: null,
      reload,
    };
    if (!enabled) return base;
    // A reload keeps the last view for this person on screen until the new read lands.
    const current = loaded && loaded.key.startsWith(`${who}|`) ? loaded : null;
    if (!current) return { ...base, status: 'loading' };
    if (current.error !== null || current.rows === null) return { ...base, status: 'error', error: current.error };
    const now = new Date(current.readAt);
    const items = current.rows
      .filter((r) => r.register === 'incidents' && r.fields.Status === EVALUATING)
      .map<IncidentToClose>((row) => {
        const checks = checksOf(row, current.actions, now);
        const open = openChecks(checks);
        const r = current.readied ? current.readied[row.ref] : undefined;
        const by = r?.upn ? lower(r.upn) : null;
        const readiedByYou = by !== null && by === who;
        return {
          row,
          checks,
          open,
          readiedBy: by,
          readiedAt: r?.at ?? null,
          readiedByYou,
          ready: open.length === 0 && by !== null && !readiedByYou,
        };
      })
      .sort((a, b) => {
        const ta = a.readiedAt ? Date.parse(a.readiedAt) : Number.POSITIVE_INFINITY;
        const tb = b.readiedAt ? Date.parse(b.readiedAt) : Number.POSITIVE_INFINITY;
        return ta !== tb ? (ta < tb ? -1 : 1) : a.row.id - b.row.id;
      });
    return {
      status: 'ready',
      items,
      readyCount: items.filter((i) => i.ready).length,
      error: null,
      followUpsNote: current.actions === null ? "The follow-ups couldn't be read, so their checks show as open. Try again." : null,
      readiedNote:
        current.readied === null
          ? "Who readied each incident couldn't be read, so none shows as ready. The record page checks it before approving."
          : null,
      reload,
    };
  }, [enabled, loaded, who, reload]);
}
